import React, { useState, useEffect } from 'react';
import { Card, Button, Switch, message, Divider, Modal, Form, Input, Select, Table, Tag, Upload } from 'antd';
import { AppstoreOutlined, LinkOutlined, SaveOutlined, PlusOutlined, EditOutlined, DeleteOutlined, EyeOutlined, EyeInvisibleOutlined, UploadOutlined } from '@ant-design/icons';
import api from '../api/axios';

const ThirdPartyAppsPage = ({ teacher }) => {
  const [thirdPartyApps, setThirdPartyApps] = useState([]);
  const [loading, setLoading] = useState(false);
  const [modalVisible, setModalVisible] = useState(false);
  const [form] = Form.useForm();
  const [editingApp, setEditingApp] = useState(null);
  const [iconUrl, setIconUrl] = useState('');

  useEffect(() => {
    fetchThirdPartyApps();
  }, []);

  const fetchThirdPartyApps = async () => {
    try {
      const response = await api.get(`/school/school/${teacher.school_id}/third-party-apps`);
      setThirdPartyApps(response.data);
    } catch (error) {
      message.error('获取第三方应用失败');
    }
  };

  const handleAddApp = () => {
    setEditingApp(null);
    form.resetFields();
    setModalVisible(true);
  };

  const handleEditApp = (app) => {
    setEditingApp(app);
    setIconUrl(app.app_icon || '');
    form.setFieldsValue(app);
    setModalVisible(true);
  };

  const handleIconUpload = async (file) => {
    const formData = new FormData();
    formData.append('file', file);
    
    try {
      const response = await api.post('/school/third-party-apps/upload-icon', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      const url = response.data.url;
      setIconUrl(url);
      form.setFieldsValue({ app_icon: url });
      message.success('图片上传成功');
    } catch (error) {
      message.error('图片上传失败');
    }
    return false;
  };

  const handleDeleteApp = async (appId) => {
    try {
      await api.delete(`/school/third-party-apps/${appId}`);
      message.success('删除成功');
      fetchThirdPartyApps();
    } catch (error) {
      message.error('删除失败');
    }
  };

  const handleToggleStatus = async (app) => {
    try {
      const newStatus = app.is_active ? 0 : 1;
      await api.put(`/school/third-party-apps/${app.id}/status`, { is_active: newStatus });
      message.success(newStatus ? '已上架' : '已下架');
      fetchThirdPartyApps();
    } catch (error) {
      message.error('操作失败');
    }
  };

  const handleSubmit = async (values) => {
    // 确保app_icon只保存URL字符串，不保存Upload组件的状态对象
    if (values.app_icon && typeof values.app_icon === 'object') {
      values.app_icon = iconUrl || '';
    }
    
    setLoading(true);
    try {
      if (editingApp) {
        await api.put(`/school/third-party-apps/${editingApp.id}`, values);
        message.success('更新成功');
      } else {
        await api.post(`/school/school/${teacher.school_id}/third-party-apps`, values);
        message.success('创建成功');
      }
      setModalVisible(false);
      form.resetFields();
      setEditingApp(null);
      fetchThirdPartyApps();
    } catch (error) {
      message.error(editingApp ? '更新失败' : '创建失败');
    } finally {
      setLoading(false);
    }
  };

  const classApps = thirdPartyApps.filter(app => app.app_type === 'class');
  const studentApps = thirdPartyApps.filter(app => app.app_type === 'student');

  const columns = [
    {
      title: '应用名称',
      dataIndex: 'app_name',
      key: 'app_name',
    },
    {
      title: '应用Key',
      dataIndex: 'app_key',
      key: 'app_key',
    },
    {
      title: '类型',
      dataIndex: 'app_type',
      key: 'app_type',
      render: (type) => (
        <Tag color={type === 'class' ? 'blue' : 'green'}>
          {type === 'class' ? '班级应用' : '学生应用'}
        </Tag>
      ),
    },
    {
      title: '状态',
      dataIndex: 'is_active',
      key: 'is_active',
      render: (status) => (
        <Tag color={status ? 'green' : 'red'}>
          {status ? '已上架' : '已下架'}
        </Tag>
      ),
    },
    {
      title: '操作',
      key: 'actions',
      render: (_, app) => (
        <div style={{ display: 'flex', gap: 8 }}>
          <Button 
            icon={<EditOutlined />} 
            size="small" 
            onClick={() => handleEditApp(app)}
          >
            编辑
          </Button>
          <Button 
            icon={app.is_active ? <EyeInvisibleOutlined /> : <EyeOutlined />} 
            size="small" 
            onClick={() => handleToggleStatus(app)}
            type={app.is_active ? 'default' : 'primary'}
          >
            {app.is_active ? '下架' : '上架'}
          </Button>
          <Button 
            icon={<DeleteOutlined />} 
            size="small" 
            danger 
            onClick={() => handleDeleteApp(app.id)}
          >
            删除
          </Button>
        </div>
      ),
    },
  ];

  return (
    <div style={{ padding: '24px' }}>
      <Card 
        title="第三方应用管理" 
        extra={
          <Button 
            type="primary" 
            icon={<PlusOutlined />} 
            onClick={handleAddApp}
          >
            添加应用
          </Button>
        }
      >
        <p style={{ marginBottom: 16, color: '#666' }}>
          管理学校的第三方应用，应用创建后默认上架，设备端只获取已上架的应用。
        </p>

        <Divider orientation="left">
          <span style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <AppstoreOutlined /> 班级应用
          </span>
        </Divider>

        {classApps.length > 0 ? (
          <div style={{ marginBottom: 24 }}>
            <Table
              columns={columns}
              dataSource={classApps}
              rowKey="id"
              pagination={false}
            />
          </div>
        ) : (
          <p style={{ color: '#999', textAlign: 'center', padding: 20, marginBottom: 24 }}>暂无班级应用</p>
        )}

        <Divider orientation="left">
          <span style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <AppstoreOutlined /> 学生应用
          </span>
        </Divider>

        {studentApps.length > 0 ? (
          <Table
            columns={columns}
            dataSource={studentApps}
            rowKey="id"
            pagination={false}
          />
        ) : (
          <p style={{ color: '#999', textAlign: 'center', padding: 20 }}>暂无学生应用</p>
        )}
      </Card>

      {/* 创建/编辑应用模态框 */}
      <Modal
        title={editingApp ? "编辑第三方应用" : "添加第三方应用"}
        open={modalVisible}
        onOk={() => form.submit()}
        onCancel={() => {
          setModalVisible(false);
          setEditingApp(null);
          form.resetFields();
        }}
        okText="确定"
        cancelText="取消"
        confirmLoading={loading}
        width={500}
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={handleSubmit}
        >
          <Form.Item
            name="app_key"
            label="应用Key"
            rules={[{ required: true, message: '请输入应用Key' }]}
          >
            <Input placeholder="请输入应用唯一标识" />
          </Form.Item>

          <Form.Item
            name="app_name"
            label="应用名称"
            rules={[{ required: true, message: '请输入应用名称' }]}
          >
            <Input placeholder="请输入应用名称" />
          </Form.Item>

          <Form.Item
            name="app_icon"
            label="应用图标"
            rules={[{ required: true, message: '请上传应用图标' }]}
            extra="支持 JPG、PNG 格式，大小不超过 2MB"
          >
            <div>
              <Upload
                accept="image/*"
                showUploadList={false}
                beforeUpload={handleIconUpload}
                listType="picture-card"
              >
                {iconUrl ? (
                  <img src={iconUrl} alt="图标" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                ) : (
                  <div>
                    <UploadOutlined />
                    <div style={{ marginTop: 8 }}>上传</div>
                  </div>
                )}
              </Upload>
              {iconUrl && (
                <div style={{ marginTop: 8, color: '#666', fontSize: 12 }}>
                  已上传图片
                </div>
              )}
            </div>
          </Form.Item>

          <Form.Item
            name="app_type"
            label="应用类型"
            rules={[{ required: true, message: '请选择应用类型' }]}
          >
            <Select>
              <Select.Option value="class">班级应用</Select.Option>
              <Select.Option value="student">学生应用</Select.Option>
            </Select>
          </Form.Item>

          <Form.Item
            name="redirect_url"
            label="跳转地址"
            rules={[{ required: true, message: '请输入跳转地址' }]}
          >
            <Input placeholder="请输入应用跳转URL" />
          </Form.Item>

          <Form.Item
            name="description"
            label="应用描述"
          >
            <Input.TextArea rows={3} placeholder="请输入应用描述（可选）" />
          </Form.Item>

          <Form.Item
            name="sort_order"
            label="排序序号"
          >
            <Input type="number" placeholder="排序序号，数字越小越靠前" />
          </Form.Item>

          {editingApp && (
            <Form.Item
              name="is_active"
              label="上架状态"
            >
              <Switch checked={editingApp.is_active} />
            </Form.Item>
          )}
        </Form>
      </Modal>
    </div>
  );
};

export default ThirdPartyAppsPage;